import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { replace } from 'connected-react-router';
import cx from 'classnames';
import useLanguage from '@clearscore/hooks.use-language';
import SvgReport from '@clearscore/icons.report';
import SvgInformation from '@clearscore/icons.information';
import SvgLogout from '@clearscore/icons.logout';
import ProgressRing from '@clearscore/rainbow.progress-ring';
import Text from '@clearscore/rainbow.text';
import Drawer from '@clearscore/rainbow.drawer';
import { actions as sessionActions } from '@clearscore/redux.session';
import { selectors as dashboardSelectors } from '@clearscore/redux.dashboard';
import { selectors as profileSelectors } from '@clearscore/redux.profile';
import { selectors as prospectSelectors } from '@clearscore/redux.prospect';
import { selectors as marketSelectors } from '@clearscore/redux.market';
import { PROFILE_STATUSES, ACCOUNT_STATUSES } from '@clearscore/constants.profile-statuses';

import lang from './lib/lang';
import marketConfig from './lib/market';
import styles from './side-menu.css';

const SideMenu = ({ isOpen, closeDrawer, market, drawerDataId }) => {
    const dispatch = useDispatch();
    const language = useLanguage(lang);
    const { fullName, accountId } = useSelector(profileSelectors.getSimple);
    const { score, scoreMax, theme } = useSelector(dashboardSelectors.getScores);
    const { accountIDVStatus, userStatus } = useSelector(profileSelectors.getStatus);
    const { email } = useSelector(prospectSelectors.getSimple);
    const activeMarket = useSelector(marketSelectors.getActiveMarket);
    const { termsLink, privacyLink, helpLink } = marketConfig[market || activeMarket];
    const isRegistration =
        ![PROFILE_STATUSES.PASS, PROFILE_STATUSES.NO_FILE].includes(accountIDVStatus) ||
        userStatus === ACCOUNT_STATUSES.DUP_LOCKED;

    const handleLogout = (e) => {
        e.stopPropagation();
        e.preventDefault();
        dispatch(sessionActions.logout());
        dispatch(replace('/login'));
    };

    return (
        <Drawer
            isOpen={isOpen}
            placement={Drawer.placements.RIGHT}
            onClose={closeDrawer}
            isCloseable
            theme={Drawer.themes.DARK}
            dataId={drawerDataId}
        >
            <div className={styles.content}>
                <div className={styles.contentPrimary}>
                    <If condition={fullName || email}>
                        <Text {...Text.presets.H2}>
                            <div className={styles.name}>{fullName || email}</div>
                        </Text>
                    </If>

                    <If condition={accountId && score}>
                        <div className={styles.progressRing}>
                            <ProgressRing value={score} maxValue={scoreMax} theme={theme} />
                            <Text type={Text.types.HUGE}>
                                <div className={cx(styles.score, theme)}>{score}</div>
                            </Text>
                        </div>
                    </If>

                    <div className={styles.menu}>
                        <If condition={accountId}>
                            <div className={styles.link}>
                                <If condition={!isRegistration}>
                                    <Text
                                        type={Text.types.MEDIUM}
                                        href="/my-account"
                                        dataId="side-menu-link-my-account"
                                        isSimpleLink
                                        theme={Text.Link.themes.DARK}
                                    >
                                        <div className={styles.iconItem}>
                                            <div className={styles.icon}>
                                                <SvgReport />
                                            </div>
                                            <Text>{language.myAccount}</Text>
                                        </div>
                                    </Text>
                                </If>
                            </div>
                        </If>

                        <div className={styles.link}>
                            <Text
                                type={Text.types.MEDIUM}
                                href={helpLink}
                                dataId="side-menu-link-help"
                                isSimpleLink
                                theme={Text.Link.themes.DARK}
                            >
                                <div className={styles.iconItem}>
                                    <div className={styles.icon}>
                                        <SvgInformation />
                                    </div>
                                    <Text>{language.help}</Text>
                                </div>
                            </Text>
                        </div>
                        <If condition={accountId}>
                            <div className={styles.link}>
                                <Text
                                    type={Text.types.MEDIUM}
                                    href="/logout"
                                    dataId="side-menu-link-logout"
                                    isSimpleLink
                                    theme={Text.Link.themes.DARK}
                                >
                                    <span onClick={handleLogout}>
                                        <div className={styles.iconItem}>
                                            <div className={styles.icon}>
                                                <SvgLogout />
                                            </div>
                                            <Text>{language.logout}</Text>
                                        </div>
                                    </span>
                                </Text>
                            </div>

                            <If condition={isRegistration}>
                                <div className={styles.delete}>
                                    <Text
                                        type={Text.types.MINI}
                                        weight={Text.weights.BOLD}
                                        href="/registration/delete-account"
                                        dataId="side-menu-link-delete-account"
                                        theme={Text.Link.themes.DARK}
                                    >
                                        {language.deleteAccount}
                                    </Text>
                                </div>
                            </If>
                        </If>
                    </div>
                </div>

                <div className={styles.footer}>
                    <Text
                        type={Text.types.TINY}
                        weight={Text.weights.BOLD}
                        href={privacyLink}
                        dataId="side-menu-link-privacy"
                        target="_blank"
                    >
                        {language.privacyPolicy}
                    </Text>

                    <Text
                        type={Text.types.TINY}
                        weight={Text.weights.BOLD}
                        href={termsLink}
                        dataId="side-menu-link-terms"
                        target="_blank"
                    >
                        {language.terms}
                    </Text>
                </div>
            </div>
        </Drawer>
    );
};

SideMenu.propTypes = {
    closeDrawer: PropTypes.func.isRequired,
    isOpen: PropTypes.bool,
    market: PropTypes.string,
    drawerDataId: PropTypes.string,
};

SideMenu.defaultProps = {
    isOpen: false,
    market: null,
    drawerDataId: '',
};

export default SideMenu;
